import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { Reveal, MaskLines, MaskLinesInView } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import eseLogo from "@/assets/brand/ese_light_logo.webp";

const REASONS = [
  { t: "Overvaluation and unrealistic price expectations", b: "The number-one deal killer. Owners price on what they need to retire, not what the market will bear. Overpriced listings go stale and often sell for 15 to 25% less than they would have at the right price from the start." },
  { t: "Owner dependency", b: "If the business can't run without the owner, buyers are purchasing a job, not a business. The IBBA reports these sell for 50 to 70% less, if they sell at all." },
  { t: "Messy or unverifiable financials", b: "Buyers pay on what they can prove, not what the owner claims. Returns showing $50K in profit get valued on $50K, regardless of actual cash flow. Creative bookkeeping kills deals." },
  { t: "No exit planning, ever", b: "Most owners never think about selling until a catastrophic event forces their hand: health, divorce, dispute, death, downturn. By then the business is usually declining and worth far less." },
  { t: "Poor or incomplete deal presentation", b: "A weak presentation signals a seller who isn't serious or prepared. Missing financials, data gaps, and vague projections kill interest fast." },
  { t: "Customer concentration", b: "If two customers are 80% of revenue, sophisticated buyers see a fragile business, not a valuable one. It is a major risk flag that discounts or kills deals." },
  { t: "Financing conditions and deal-structure rigidity", b: "Sellers who demand all-cash eliminate roughly 78% of buyers; only about 23% of small-business acquisitions close all-cash. Inflexibility on terms kills viable deals." },
  { t: "Untrained or unqualified brokers and advisors", b: "Many listings are handled by realtors or accountants with no real valuation or deal-making expertise. They overprice, under-prepare, and under-perform." },
  { t: "Fundamental business flaws", b: "Declining revenue, thin margins, outdated models, or unresolved legal and environmental issues make a business unsalable without remediation first." },
  { t: "Market timing and economic conditions", b: "Sellers who wait for a crisis, personal or economic, hit the market at the worst moment. Businesses sell best when they are growing, not struggling." },
];

const AREAS = [
  { t: "Clean, credible financials", b: "Three to five years of clean statements; tax returns that reconcile; personal and one-time expenses removed as add-backs; revenue broken out by product, customer, and channel; normalized EBITDA. If the numbers are messy, buyers discount heavily or walk." },
  { t: "Reduced owner dependency", b: "Documented processes and SOPs; delegated decision authority; a strong second-in-command; key relationships not tied solely to the owner. The more transferable the business, the higher the multiple." },
  { t: "Stable, strengthening revenue", b: "Long-term contracts where possible; recurring revenue; reduced concentration risk; a demonstrated growth trend. Revenue concentration above 20 to 30% from one client is a red flag." },
  { t: "Organized legal and corporate documentation", b: "Formation and operating agreements; shareholder or partnership agreements; customer and vendor contracts; employment agreements and non-competes; IP and warranty documentation; any pending-litigation disclosure. Disorganization here slows deals and erodes trust." },
  { t: "Operational efficiency", b: "Strong gross margins; controlled overhead; scalable systems; clean inventory management where applicable; minimal deferred maintenance. Buyers want stability, not hidden problems." },
  { t: "A pre-sale valuation and deal strategy", b: "A professional valuation (asset, income, or market approach); the ideal buyer type identified (strategic or financial); tax implications understood; post-sale involvement decided (clean exit or transition); earn-outs or seller financing planned if needed. Entering the market without a strategy leaves money on the table." },
];

export default function ExitAndSell() {
  return (
    <>
      {/* Hero */}
      <section className="relative bg-blue text-cream grain pt-40 pb-20 md:pt-52 md:pb-28 overflow-hidden">
        <div className="container-editorial relative z-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-cream p-2 shrink-0">
              <img src={eseLogo} alt="Exit Strategy Experts" className="h-12 w-auto" />
            </div>
            <p className="eyebrow text-amber-bright">The Destination · Flagship</p>
          </div>
          <h1 className="font-serif font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight">
            <MaskLines lines={["Exit on"]} delay={0.1} />
            <MaskLines lines={["your terms."]} className="italic text-amber-bright" delay={0.26} />
          </h1>
          <Reveal delay={0.5}>
            <p className="font-serif italic text-2xl md:text-3xl mt-8 text-cream/85 max-w-2xl">
              Where the whole journey leads.
            </p>
            <p className="font-body text-lg text-cream/70 mt-6 max-w-2xl leading-relaxed">
              Most owners never hear the reality of what it takes to sell a business. Here it is, and
              here is exactly how you get ready.
            </p>
          </Reveal>
          <Reveal delay={0.6}>
            <Link
              to="/contact"
              data-testid="exit-hero-cta"
              className="mt-10 inline-flex items-center gap-2 bg-amber-deep text-cream px-9 py-4 eyebrow text-xs hover:bg-cream hover:text-blue transition-colors"
            >
              Book a Consultation <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Section 1: the reality */}
      <section data-testid="exit-reality" className="bg-cream py-24 md:py-32">
        <div className="container-editorial">
          <div className="max-w-3xl mb-14">
            <Reveal>
              <p className="eyebrow text-amber-deep mb-6">The Reality Most Owners Never Hear</p>
            </Reveal>
            <MaskLinesInView lines={["70 to 80% of businesses", "that go to market"]} className="font-serif font-bold text-4xl md:text-5xl text-blue leading-[1.04]" />
            <MaskLinesInView lines={["never sell."]} className="font-serif font-bold italic text-4xl md:text-5xl text-amber-deep leading-[1.04] mt-1" />
            <Reveal delay={0.15}>
              <p className="font-body text-lg text-charcoal/75 mt-8 leading-relaxed">
                According to the Exit Planning Institute, 70 to 80% of businesses that go to market
                never sell, and another 10% simply close their doors. The gap between the ones that
                sell and the ones that don't isn't luck; it's preparation. Here are the ten reasons
                businesses fail to sell.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-blue/10 border border-blue/10">
            {REASONS.map((r, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06} className="bg-cream p-8 md:p-10">
                <div className="flex items-start gap-5">
                  <span className="font-serif font-bold text-4xl md:text-5xl text-amber/40 leading-none w-14 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-serif font-bold text-xl md:text-2xl text-blue leading-snug">{r.t}</h3>
                    <p className="font-body text-charcoal/70 mt-3 leading-relaxed">{r.b}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="font-serif italic text-2xl md:text-3xl text-blue mt-12 max-w-3xl">
              Every one of these is fixable, with a plan and enough runway.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Section 2: the six areas */}
      <section data-testid="exit-six-areas" className="bg-blue-deep text-cream py-24 md:py-32 grain relative overflow-hidden">
        <div className="container-editorial relative z-10">
          <div className="max-w-3xl mb-14">
            <Reveal>
              <p className="eyebrow text-amber-bright mb-6">The Answer to the Ten</p>
            </Reveal>
            <MaskLinesInView lines={["The six areas you need"]} className="font-serif font-bold text-4xl md:text-5xl text-cream leading-[1.04]" />
            <MaskLinesInView lines={["to shine to exit on top."]} className="font-serif font-bold italic text-4xl md:text-5xl text-amber-bright leading-[1.04] mt-1" />
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-cream/10 border border-cream/10">
            {AREAS.map((a, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06} className="bg-blue-deep p-8 md:p-10">
                <div className="flex items-start gap-5">
                  <Check size={22} className="text-amber-bright mt-2 shrink-0" />
                  <div>
                    <div className="flex items-baseline gap-3">
                      <span className="font-serif text-xl text-amber-bright/70">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="font-serif font-bold text-xl md:text-2xl text-cream leading-snug">{a.t}</h3>
                    </div>
                    <p className="font-body text-cream/70 mt-3 leading-relaxed">{a.b}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: how we get you there */}
      <section data-testid="exit-how" className="bg-cream py-24 md:py-32">
        <div className="container-editorial grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-start">
          <div>
            <Reveal>
              <p className="eyebrow text-amber-deep mb-6">How We Get You There</p>
            </Reveal>
            <MaskLinesInView lines={["One advisor.", "One fee."]} className="font-serif font-bold text-4xl md:text-6xl text-blue leading-[1.02]" />
            <MaskLinesInView lines={["Six areas, built."]} className="font-serif font-bold italic text-4xl md:text-6xl text-amber-deep leading-[1.02]" />
          </div>
          <Reveal delay={0.15}>
            <p className="font-body text-lg text-charcoal/75 leading-relaxed">
              This is the work of the Exit engagement: an independent valuation, a bank-ready and
              buyer-ready package, multiple engineered deal structures, a negotiation playbook, a
              due-diligence defense handbook, a full transaction management guide, and post-close
              protection protocols. One advisor, one fee, the six areas above built with you before a
              buyer ever asks a question.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-5 sm:items-center">
              <Link
                to="/contact"
                data-testid="exit-how-cta"
                className="group relative overflow-hidden bg-blue text-cream px-9 py-4 eyebrow text-xs inline-flex items-center justify-center gap-2 w-fit"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Book a Consultation <ArrowUpRight size={16} />
                </span>
                <span className="absolute inset-0 bg-amber-deep translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              </Link>
              <Link
                to="/resources"
                data-testid="exit-see-value"
                className="eyebrow text-xs text-blue hover:text-amber-deep link-underline inline-flex items-center gap-2 transition-colors"
              >
                See what one fee replaces <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
